import { CommonModule } from "@angular/common";
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from "@angular/core";
import _ from "lodash";
import { TreeNode } from "../../core/models/treeNode";
import { SortByKeyPipe } from "../../core/pipes/sort-by-key.pipe";
import { MaterialModule } from "../../core/shared/material.module";
import { SharedComponents } from "../shared.module";

@Component({
  selector: "app-tree-view",
  standalone: true,
  imports: [CommonModule, SortByKeyPipe, ...SharedComponents, MaterialModule],
  templateUrl: "./tree-view.component.html",
  styleUrls: ["./tree-view.component.scss"],
})
export class TreeViewComponent implements OnChanges {
  @Input() nodes: TreeNode[] = [];
  @Input() selectedNode!: TreeNode;
  @Input() disableNodeSelect: boolean = false;
  @Input() disableNodeTick: boolean = false;
  @Input() isloading: boolean = false;
  @Input() idName?:string="";
  @Input() disableGroupHighlight = false;
  @Input() highlightOnExpand = false;
  @Input() isSearch = false;
  @Input() isEditor = false;
  @Input() draggable = false;
  @Input() hideIconForChildren = false;
  @Input() singleExpandMode = false;
  @Input() appendParentToSelectedNode = true;
  @Input() removeGreenLeftBorder = false;
   @Input() removeBg = false;
  @Output() menuItemSelected = new EventEmitter<TreeNode>();
  @Output() onItemTick = new EventEmitter<TreeNode>();
  @Output() actionableItem = new EventEmitter<any>();

  expandedNodes = new Set<string>();
  private _collapsingId: string | null = null;

  contextMenuIndex: number | null = null;
  contextMenuPosition = { x: 0, y: 0 };
  fillStyle = 'font-variation-settings: "FILL" 1, "wght" 400, "GRAD" 0, "opsz" 24;'

  ngOnChanges(changes: SimpleChanges): void {
    console.log(this.nodes);
    if (changes["selectedNode"] && !changes["selectedNode"].isFirstChange()) {
      console.log("selectedNode has changed:", this.selectedNode);
      if (this._collapsingId && this.selectedNode?.id === this._collapsingId) {
        this._collapsingId = null;
        return;
      }
      this._collapsingId = null;
      setTimeout(() => {
        if (this.selectedNode) {
          this.expandedNodes.add(this.selectedNode.id);
        }
      });
    }
  }

  toggleExpand(node: TreeNode) {
    if (this.expandedNodes.has(node.id)) {
      if(node?.checked){
        return;
      }
      this._collapsingId = node.id;
      this.expandedNodes.delete(node.id);
      node.type === "project"
        ? this.selectNode(undefined)
        : this.selectNode(node);
    } else {
      if (this.singleExpandMode && node.children?.length) {
        // Accordion behavior: keep only the latest expanded sibling node open.
        this.expandedNodes.clear();
      }
      if (node.type === "project") {
        this.collapseAll();
      }
      this.expandedNodes.add(node.id);
      if(this.selectedNode) {
        let parent = _.cloneDeep(this.selectedNode);
        parent.children = [];
        if(this.appendParentToSelectedNode) {
          node.parent = parent;
        }
      }
      this.selectNode(node);
    }
  }

  selectNode(node: any) {
    if (this.disableNodeSelect) {
      return;
    }
    this.menuItemSelected.emit(node);
  }
  onCheckboxChange(node: any, event: any): void {
    node.checked = event.target.checked;

    if (node.children && node.children.length > 0) {
      node.children.forEach((child:any) => {
        child.checked = node.checked;
      });
    }
    // Get all checked nodes
    let checkedItems:any = [];
    this.nodes.forEach((n:any) => {
      if (n.checked) checkedItems.push(n);
      if (n.children?.length) checkedItems.push(...node.children);
    });
    checkedItems = checkedItems.filter((n:any) => n.checked);
    this.actionableItem.emit({ key: 'checked' , node: checkedItems });

  }
  
  expandNode(node: TreeNode, isCollapse: boolean = false) {
    if (!isCollapse && !this.expandedNodes.has(node.id)) {
      this.expandedNodes.add(node.id);
    }
    if(isCollapse && this.expandedNodes.has(node.id)) {
      this.expandedNodes.delete(node.id);
    }
  }

  onMenuItemSelected(item: TreeNode) {
    this.menuItemSelected.emit(item);
  }

  collapseAll() {
    this.expandedNodes.clear();
    this.selectNode(undefined);
  }

  openAddItemsPopup(item: TreeNode) {
    console.log("Emitting addIconSelected event with item:", item);
    this.menuItemSelected.emit({ ...item, popupType: "add" });
  }

  toggleContextMenu(event: MouseEvent, index: number) {
    this.contextMenuIndex = this.contextMenuIndex === index ? null : index;
    const menuWidth = 180;
    const posX = event.clientX - menuWidth;
    this.contextMenuPosition = { x: posX, y: event.clientY + 10 };
  }

  handleContextMenuAction(item: TreeNode, action: string): void {
    console.log(event, action);
    this.contextMenuIndex = null;
    this.menuItemSelected.emit({ ...item, popupType: action });
  }

  handleActionableItemAction(item: TreeNode, actionableItem: any): void {
    actionableItem.isloading = true;
    this.actionableItem.emit({ key: actionableItem.key , node: item });
  }

  onActionableNodeClicked(event: any) {
    this.actionableItem.emit(event);
  }

  onImgError(event: any) { 
    event.target.src = 'assets/images/folder.png';
  }

  onTick(node: TreeNode) {
    if(this.disableNodeTick) {
      return;
    }
    node.isTicked = !node.isTicked;
    this.onItemTick.emit(node);
  }

  onChildTicked(event: any) {
    this.onItemTick.emit(event);
  }

  getNodeStyle(node: TreeNode) {
    let level = node.level || 0;
    return node.style ? {...node.style, width: `${200 - (level*20)}px`} : { width: `${200 - (level*20)}px` }; 
  }

  getAssetIcon(node: any): string {
    if (this.selectedNode && this.selectedNode.id === node.id) {
      return node.assetIcon.replace('.svg', '-light.svg');
    }
    return node.assetIcon;
  }

  getPrefixIcon(node: any): string {
    if (this.selectedNode && this.selectedNode.id === node.id) {
      return node.prefixIcon.replace('.svg', '-light.svg');
    }
    return node.prefixIcon;
  }

  getActionIcon(node: any): string {
    if (this.selectedNode && this.selectedNode.id === node.id) {
      return node.actionIcon.replace('.svg', '-light.svg');
    }
    return node.actionIcon;
  }


  getOperationClass(type: string): string {
    switch (type) {
      case 'GET':
        return 'operation-get';
      case 'POST':
        return 'operation-post';
      case 'PUT':
        return 'operation-put';
      case 'PATCH':
          return 'operation-patch';
      case 'DELETE':
        return 'operation-delete';
      default:
        return '';
    }
  }  
  
}
